import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { TopBar } from '../../components/TopBar.tsx';
import { BottomNav } from '../../components/BottomNav.tsx';
import { FeedbackSummary } from '../../components/FeedbackSummary.tsx';
import type { Feedback } from '../../ratings.ts';
import { formatDateRange, MISSION_STATUS_LABELS } from '../../format.ts';
import type { Mission, Organization } from '../../types.ts';

export function RestoDashboard() {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null | 'loading'>('loading');
  const [missions, setMissions] = useState<Mission[]>([]);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  async function load() {
    const orgs = await api.get<{ organizations: Organization[] }>('/organizations/mine');
    if (orgs.organizations.length === 0) {
      setOrganization(null);
      return;
    }
    const org = orgs.organizations[0];
    setOrganization(org);
    const missionsRes = await api.get<{ missions: Mission[] }>(`/organizations/${org.id}/missions`);
    setMissions(missionsRes.missions);
    api.get<Feedback>(`/feedback/org/${org.id}`).then(setFeedback).catch(() => undefined);
  }

  useEffect(() => {
    load().catch((err) => {
      if (!(err instanceof ApiError)) throw err;
    });
  }, []);

  async function createOrganization() {
    setCreating(true);
    try {
      const orgRes = await api.post<{ organization: Organization }>('/organizations', {
        name,
        orgType: 'RESTAURANT',
      });
      await api.post(`/organizations/${orgRes.organization.id}/establishments`, {
        name,
        address,
        city,
        postalCode,
      });
      await load();
    } finally {
      setCreating(false);
    }
  }

  if (organization === 'loading') {
    return (
      <div className="app-shell">
        <TopBar title="Mon Extra" logo />
        <div className="screen-content empty-state">Chargement…</div>
      </div>
    );
  }

  if (organization === null) {
    return (
      <div className="app-shell">
        <TopBar title="Bienvenue" logo />
        <div className="screen-content">
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontWeight: 800 }}>Créez votre établissement</div>
            <div>
              <label className="field-label" htmlFor="name">Nom de l'établissement</label>
              <input id="name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="address">Adresse</label>
              <input id="address" className="input" value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label className="field-label" htmlFor="city">Ville</label>
                <input id="city" className="input" value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div style={{ width: 110 }}>
                <label className="field-label" htmlFor="postalCode">Code postal</label>
                <input id="postalCode" className="input" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
              </div>
            </div>
            <button className="btn btn-primary" onClick={createOrganization} disabled={creating || !name || !address || !city || !postalCode}>
              {creating ? 'Création…' : 'Créer'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <TopBar title={organization.name} logo />
      <div className="screen-content">
        <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Bonjour {user?.firstName}</div>

        <Link to="/resto/missions/new" className="btn btn-primary">Publier une nouvelle mission</Link>

        <Link to="/resto/carte" className="card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
          <div style={{ fontWeight: 800 }}>Moyen de paiement</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>Carte bancaire utilisée pour garantir vos missions : ajouter ou remplacer.</div>
        </Link>

        <div>
          <div style={{ fontSize: 14, fontWeight: 800, padding: '0 2px 8px' }}>Vos missions</div>
          {missions.length === 0 && <div className="empty-state">Aucune mission publiée pour l'instant.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {missions.map((m) => (
              <Link key={m.id} to={`/resto/missions/${m.id}`} className="card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: 14.5 }}>{m.title}</div>
                  <span className="badge badge-neutral">{MISSION_STATUS_LABELS[m.status] ?? m.status}</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {formatDateRange(m.starts_at, m.ends_at)}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {feedback && feedback.count > 0 && (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontWeight: 800 }}>Avis des extras sur votre établissement</div>
            <FeedbackSummary feedback={feedback} />
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
